import { assertAuthSessionEpoch } from '@drama/api-sdk'

/**
 * @typedef {import('@drama/api-sdk').ApiError} ApiError
 * @typedef {import('@drama/api-sdk').TokenStore} TokenStore
 */

/**
 * @param {{
 *   basePath: string,
 *   baseUrl: string,
 *   tokenStore: {
 *     getAccessToken: () => string|null,
 *     getSessionEpoch?: () => string|null,
 *     getDeviceId: () => string,
 *     refresh: () => Promise<string>,
 *   },
 *   getLocale?: () => string,
 *   fetchImpl?: typeof fetch,
 * }} opts
 */
export function createCallchainHttp(opts) {
  if (!opts || typeof opts.basePath !== 'string') {
    throw new Error('createCallchainHttp: opts.basePath is required')
  }
  if (typeof opts.baseUrl !== 'string') {
    throw new Error('createCallchainHttp: opts.baseUrl is required')
  }
  if (!opts.tokenStore || typeof opts.tokenStore.getAccessToken !== 'function') {
    throw new Error('createCallchainHttp: opts.tokenStore is required')
  }
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch
  if (typeof fetchImpl !== 'function') {
    throw new Error('createCallchainHttp: fetch is unavailable')
  }
  const inflightRefresh = new Map()

  function refreshOnce(epoch, assertSession) {
    if (inflightRefresh.has(epoch)) return inflightRefresh.get(epoch)
    const pending = Promise.resolve().then(() => {
      assertSession()
      return opts.tokenStore.refresh()
    }).finally(() => {
      if (inflightRefresh.get(epoch) === pending) inflightRefresh.delete(epoch)
    })
    inflightRefresh.set(epoch, pending)
    return pending
  }

  /**
   * @param {string} path
   * @param {{ query?: Record<string, string|number|boolean|null|undefined> }} request
   */
  function buildUrl(path, request) {
    const fullPath = `${opts.basePath}${path}`
    const query = request.query
    if (!opts.baseUrl || opts.baseUrl === '/') {
      return appendQuery(fullPath, query)
    }
    return appendQuery(new URL(fullPath, ensureTrailingBase(opts.baseUrl)).toString(), query)
  }

  /**
   * @param {string} method
   * @param {string} path
   * @param {{ query?: Record<string, string|number|boolean|null|undefined>, body?: unknown, routeId: string }} request
   */
  async function requestJson(method, path, request) {
    const { res, assertSession } = await withTokenRefresh(access_token => fetchImpl(buildUrl(path, request), {
      method,
      headers: buildHeaders(request, access_token),
      body: request.body === undefined ? undefined : JSON.stringify(request.body),
    }))
    if (!res.ok) {
      const error = await toError(res)
      assertSession()
      throw error
    }
    const data = res.status === 204 ? undefined : await res.json()
    assertSession()
    return data
  }

  /**
   * @param {string} path
   * @param {{ query?: Record<string, string|number|boolean|null|undefined>, routeId: string }} request
   */
  async function requestText(path, request) {
    const { res, assertSession } = await withTokenRefresh(access_token => fetchImpl(buildUrl(path, request), {
      method: 'GET',
      headers: buildHeaders(request, access_token),
    }))
    if (!res.ok) {
      const error = await toError(res)
      assertSession()
      throw error
    }
    const data = await res.text()
    assertSession()
    return data
  }

  async function requestBlob(path, request) {
    const { res, assertSession } = await withTokenRefresh(access_token => fetchImpl(buildUrl(path, request), {
      method: 'GET',
      headers: buildHeaders(request, access_token),
    }))
    if (!res.ok) {
      const error = await toError(res)
      assertSession()
      throw error
    }
    const data = await res.blob()
    assertSession()
    return data
  }

  /**
   * Access token 过期时只刷新一次并重放原请求。并发 401 共用同一个
   * refresh promise，避免 refresh token 轮转时旧 token 被重复使用。
   *
   * @param {(access_token?:string|null) => Promise<Response>} send
   */
  async function withTokenRefresh(send) {
    const guarded = typeof opts.tokenStore.getSessionEpoch === 'function'
    const epoch = guarded ? opts.tokenStore.getSessionEpoch() : undefined
    const assertSession = () => { if (guarded) assertAuthSessionEpoch(epoch, opts.tokenStore.getSessionEpoch()) }
    const sendCurrentSession = () => {
      assertSession()
      const access_token = guarded ? opts.tokenStore.getAccessToken() : undefined
      assertSession()
      return send(access_token)
    }
    let res = await sendCurrentSession()
    assertSession()
    if (res.status !== 401) return { res, assertSession }
    try {
      await refreshOnce(epoch, assertSession)
      assertSession()
    } catch (error) {
      assertSession()
      throw error
    }
    res = await sendCurrentSession()
    assertSession()
    return { res, assertSession }
  }

  /**
   * @param {{ body?: unknown, routeId: string }} request
   * @param {string|null|undefined} access_token
   */
  function buildHeaders(request, access_token) {
    /** @type {Record<string, string>} */
    const headers = {
      'x-device-id': opts.tokenStore.getDeviceId(),
      'x-locale': opts.getLocale ? opts.getLocale() : 'en',
      'x-callchain-route-id': request.routeId,
    }
    const token = access_token === undefined ? opts.tokenStore.getAccessToken() : access_token
    if (token) headers.authorization = `Bearer ${token}`
    if (request.body !== undefined) headers['content-type'] = 'application/json'
    return headers
  }

  return {
    /** @param {string} path @param {{ query?: Record<string, string|number|boolean|null|undefined>, routeId: string }} request */
    get(path, request) {
      return requestJson('GET', path, request)
    },
    /** @param {string} path @param {{ body?: unknown, routeId: string }} request */
    post(path, request) {
      return requestJson('POST', path, request)
    },
    /** @param {string} path @param {{ body?: unknown, routeId: string }} request */
    put(path, request) {
      return requestJson('PUT', path, request)
    },
    /** @param {string} path @param {{ body?: unknown, routeId: string }} request */
    patch(path, request) {
      return requestJson('PATCH', path, request)
    },
    /** @param {string} path @param {{ query?: Record<string, string|number|boolean|null|undefined>, routeId: string }} request */
    delete(path, request) {
      return requestJson('DELETE', path, request)
    },
    getText: requestText,
    getBlob: requestBlob,
  }
}

/**
 * @param {string} url
 * @param {Record<string, string|number|boolean|null|undefined>|undefined} query
 */
function appendQuery(url, query) {
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url
}

/** @param {string} base */
function ensureTrailingBase(base) {
  return base.endsWith('/') ? base : `${base}/`
}

/** @param {Response} res */
async function toError(res) {
  let message = res.statusText || `HTTP ${res.status}`
  let code = 'HTTP_ERROR'
  let max_reason_length
  try {
    const payload = await res.json()
    if (payload.error && typeof payload.error.message === 'string') {
      message = payload.error.message
    }
    if (payload.error && typeof payload.error.code === 'string') {
      code = payload.error.code
    }
    if (Number.isSafeInteger(payload.error?.max_reason_length) && payload.error.max_reason_length > 0) {
      max_reason_length = payload.error.max_reason_length
    }
  } catch {
    message = res.statusText || `HTTP ${res.status}`
  }
  const error = new Error(message)
  error.code = code
  error.statusCode = res.status
  if (max_reason_length !== undefined) error.max_reason_length = max_reason_length
  return error
}
